import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  Group,
  GroupMember,
  GroupRole,
  UserGroupMembership,
  GroupCategoryRecord,
  GroupVideo,
} from '@/types';
import { getUploadErrorMessage, uploadFilesSequentially } from '@/lib/upload';
import { fetchStrapiCollection } from '@/lib/strapi';
import { canCreateGroupsForUser } from '@/utils/age';

const baseUrl = '/api/strapi';
const POSTABLE_GROUP_ROLES = new Set<GroupRole>(['admin', 'editor']);

/** A `groups` row as returned by Strapi, before mapping to the store's Group type. */
interface RawGroupRow {
  id: number;
  documentId: string;
  name: string;
  description: string;
  // Legacy json field; only used to surface pre-backfill records in the UI
  // until the seed bootstrap copies them to the relation. Never written.
  category?: string | string[];
  categories?: GroupCategoryRecord[];
  acceptingMembers: boolean;
  memberListVisible?: boolean;
  eventListVisible?: boolean;
  isPrivate?: boolean;
  memberApprovalMode?: 'auto' | 'manual';
  websiteLink?: string;
  tags?: string[];
  videos?: GroupVideo[];
  coverImage?: { id?: number; url: string; alternativeText?: string };
  avatar?: { id?: number; url: string; alternativeText?: string };
  images?: Array<{ id: number; url: string; alternativeText?: string }>;
  rulesText?: string;
  group_members?: { count: number };
  events?: { count: number };
  createdAt: string;
  updatedAt: string;
}

/** A `group-members` row as returned by Strapi for the membership index. */
interface RawGroupMemberRow {
  user?: { id: number };
  group?: { id: number; documentId: string };
  role: GroupRole;
}

function uploadGroupImages(files: File[]): Promise<number[]> {
  return uploadFilesSequentially(files, 'Failed to upload images');
}

export function getGroupsWhereUserCanPostFromState(groups: Group[], members: GroupMember[], userId: number): Group[] {
  const postableGroupDocumentIds = new Set(
    members
      .filter((member) => member.userId === userId && POSTABLE_GROUP_ROLES.has(member.role))
      .map((member) => member.groupDocumentId),
  );

  return groups.filter((group) => postableGroupDocumentIds.has(group.documentId));
}

/** Map raw Strapi `group-members` rows to the store's GroupMember shape. */
function mapRawMembers(rawMembers: RawGroupMemberRow[]): GroupMember[] {
  return rawMembers
    .map((item) => ({
      userId: item.user?.id ?? 0,
      groupId: item.group?.id ?? 0,
      groupDocumentId: item.group?.documentId ?? '',
      role: item.role,
    }))
    .filter((m) => m.userId && m.groupId && m.groupDocumentId);
}

interface CreateGroupData {
  name: string;
  description: string;
  rulesText?: string;
  /** Canonical group-category slugs. Resolved to documentIds in createGroup. */
  categorySlugs: string[];
  websiteLink?: string;
  acceptingMembers: boolean;
  memberListVisible: boolean;
  eventListVisible: boolean;
  // MWBI-739 — true keeps the group out of the public directory browse list.
  // Optional: when omitted the group is created public (Strapi schema default).
  isPrivate?: boolean;
  memberApprovalMode: 'auto' | 'manual';
  image: File | null;
  images?: File[];
  avatar?: File | null;
}

interface UpdateGroupData {
  name?: string;
  description?: string;
  /** Canonical group-category slugs. `undefined` leaves the relation untouched; `[]` clears it. */
  categorySlugs?: string[];
  websiteLink?: string;
  acceptingMembers?: boolean;
  memberListVisible?: boolean;
  eventListVisible?: boolean;
  isPrivate?: boolean;
  memberApprovalMode?: 'auto' | 'manual';
  rulesText?: string;
  image?: File | null;
  images?: File[];
  avatar?: File | null;
  existingImageIds?: number[];
  newImages?: File[];
}

interface GroupState {
  groups: Group[];
  // Available group-categories (canonical list from api::group-category).
  // Drives every category dropdown + filter chip in the UI. Fetched once
  // per session by fetchCategories().
  categories: GroupCategoryRecord[];
  categoriesLoaded: boolean;
  // Site-wide membership index — every user's approved memberships. Only the
  // views that genuinely need other users' data read this (the users list and
  // other people's profiles, via getUserGroups). It is large; do not depend on
  // it for current-user checks — use myMemberships instead.
  members: GroupMember[];
  membersLoaded: boolean;
  // The signed-in user's approved memberships only — the fast path for every
  // current-user check. Populated by fetchMyMemberships.
  myMemberships: GroupMember[];
  myMembershipsLoaded: boolean;
  groupsLoaded: boolean;
  loading: boolean;
  // 'all' or a category slug. Slugs are runtime data (from `categories`).
  selectedCategory: string;
  searchQuery: string;
  setSelectedCategory: (category: string) => void;
  setSearchQuery: (query: string) => void;
  fetchGroups: (force?: boolean) => Promise<void>;
  fetchCategories: (force?: boolean) => Promise<void>;
  fetchGroupMembers: (force?: boolean) => Promise<void>;
  fetchMyMemberships: (userId: number, force?: boolean) => Promise<void>;
  updateGroupInStore: (groupId: number, updates: Partial<Group>) => void;
  createGroup: (data: CreateGroupData, userId: number) => Promise<{ success: boolean; groupDocumentId?: string; error?: string }>;
  updateGroup: (documentId: string, data: UpdateGroupData) => Promise<{ success: boolean; error?: string }>;
  deleteGroup: (documentId: string) => Promise<boolean>;
  // getUserGroups reads the site-wide `members` index — it is the one helper
  // that must answer for arbitrary users (other people's profiles).
  getUserGroups: (userId: number) => UserGroupMembership[];
  // The helpers below read `myMemberships` — pass the signed-in user's id.
  getUserRole: (userId: number, groupId: number) => GroupRole | null;
  isUserMemberOfGroup: (userId: number, groupId: number) => boolean;
  canUserCreateOpportunity: (userId: number) => boolean;
  getGroupsWhereUserCanPost: (userId: number) => Group[];
  canUserManageOpportunity: (userId: number, groupId: number) => boolean;
}

export const useGroupStore = create<GroupState>()(
  persist(
    (set, get) => ({
  groups: [],
  categories: [],
  categoriesLoaded: false,
  members: [],
  membersLoaded: false,
  myMemberships: [],
  myMembershipsLoaded: false,
  groupsLoaded: false,
  loading: false,
  selectedCategory: 'all',
  searchQuery: '',

  setSelectedCategory: (category) => set({ selectedCategory: category }),
  setSearchQuery: (query) => set({ searchQuery: query }),

  fetchCategories: async (force = false) => {
    if (!force && get().categoriesLoaded) return;
    try {
      const rows = await fetchStrapiCollection<GroupCategoryRecord>('group-categories', {
        fields: ['id', 'documentId', 'name', 'slug', 'order', 'archived'],
        sort: ['order:asc', 'name:asc'],
      });
      set({ categories: rows, categoriesLoaded: true });
    } catch (error) {
      console.error('Error fetching group categories:', error);
    }
  },

  fetchGroups: async (force = false) => {
    if (!force && get().groupsLoaded) return;
    try {
      set({ loading: true });
      const today = new Date().toISOString().split('T')[0];
      const rawGroups = await fetchStrapiCollection<RawGroupRow>('groups', {
        filters: { deletedAt: { $null: true } },
        populate: {
          coverImage: true,
          avatar: true,
          images: true,
          categories: { fields: ['id', 'documentId', 'name', 'slug', 'order', 'archived'] },
          group_members: { count: true, filters: { approvalStatus: { $eq: 'approved' } } },
          events: { count: true, filters: { date: { $gte: today }, deletedAt: { $null: true } } },
        },
      });

      const groups: Group[] = rawGroups.map((item) => ({
        id: item.id,
        documentId: item.documentId,
        name: item.name,
        description: item.description,
        categories: item.categories ?? [],
        // legacy fallback for pre-backfill records; UI prefers `categories`
        ...(item.category !== undefined && item.category !== null ? { category: item.category } : {}),
        acceptingMembers: item.acceptingMembers,
        memberListVisible: item.memberListVisible ?? true,
        eventListVisible: item.eventListVisible ?? true,
        isPrivate: item.isPrivate ?? false,
        memberApprovalMode: item.memberApprovalMode ?? 'manual',
        websiteLink: item.websiteLink,
        tags: item.tags,
        videos: item.videos || [],
        coverImage: item.coverImage
          ? { url: item.coverImage.url, ...(item.coverImage.id !== undefined && { id: item.coverImage.id }), ...(item.coverImage.alternativeText !== undefined && { alternativeText: item.coverImage.alternativeText }) }
          : undefined,
        avatar: item.avatar
          ? { url: item.avatar.url, ...(item.avatar.id !== undefined && { id: item.avatar.id }), ...(item.avatar.alternativeText !== undefined && { alternativeText: item.avatar.alternativeText }) }
          : undefined,
        ...(item.images && item.images.length > 0
          ? {
              images: item.images.map((image) => ({
                id: image.id,
                url: image.url,
                alternativeText: image.alternativeText,
              })),
            }
          : {}),
        ...(item.rulesText !== undefined && item.rulesText !== null ? { rulesText: item.rulesText } : {}),
        memberCount: item.group_members?.count ?? 0,
        eventsCount: item.events?.count ?? 0,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
      }));

      set({ groups, loading: false, groupsLoaded: true });
    } catch (error) {
      console.error('Error fetching groups:', error);
      set({ loading: false });
    }
  },

  // Site-wide membership index — every approved membership across every group.
  // This is large (it grows with the whole user base) and exhaustively paged.
  // Only call it from views that genuinely need other users' memberships (the
  // users list, other people's profiles). For current-user checks, use
  // fetchMyMemberships — see MWBI-744 / the group-member pagination work.
  fetchGroupMembers: async (force = false) => {
    if (!force && get().membersLoaded) return;
    try {
      const rawMembers = await fetchStrapiCollection<RawGroupMemberRow>('group-members', {
        filters: { approvalStatus: { $eq: 'approved' } },
        populate: {
          user: { fields: ['id'] },
          group: { fields: ['id', 'documentId'] },
        },
      });

      set({ members: mapRawMembers(rawMembers), membersLoaded: true });
    } catch (error) {
      console.error('Error fetching group members:', error);
    }
  },

  // Fast path: load ONLY the signed-in user's approved memberships. Backs every
  // current-user check (can-I-create, my role in a group) without paging the
  // whole site-wide membership collection. Pass the current user's id.
  fetchMyMemberships: async (userId: number, force = false) => {
    if (!userId) return;
    if (!force && get().myMembershipsLoaded) return;
    try {
      const rawMembers = await fetchStrapiCollection<RawGroupMemberRow>('group-members', {
        filters: {
          user: { id: { $eq: userId } },
          approvalStatus: { $eq: 'approved' },
        },
        populate: {
          user: { fields: ['id'] },
          group: { fields: ['id', 'documentId'] },
        },
      });

      set({ myMemberships: mapRawMembers(rawMembers), myMembershipsLoaded: true });
    } catch (error) {
      console.error('Error fetching current-user memberships:', error);
    }
  },

  updateGroupInStore: (groupId: number, updates: Partial<Group>) => {
    set((state) => ({
      groups: state.groups.map((group) =>
        group.id === groupId ? { ...group, ...updates } : group
      ),
    }));
  },

  createGroup: async (data, userId) => {
    try {
      // 1. Check user age eligibility
      const userRes = await fetch(`${baseUrl}/users/${userId}`);
      if (!userRes.ok) throw new Error('Failed to fetch user data');
      const userData = await userRes.json();

      // Users under 16 cannot create groups (T&C §4.1). Birthdate takes precedence over
      // ageGroup so that 16-year-olds whose legacy under_18 bucket hasn't been promoted yet
      // are not incorrectly blocked.
      if (!canCreateGroupsForUser(userData)) {
        return { success: false, error: 'You must be 16 years or older to create a group.' };
      }

      // 2. Upload images if provided
      const filesToUpload = data.images?.length ? data.images : (data.image ? [data.image] : []);
      const imageIds = await uploadGroupImages(filesToUpload);
      const imageId = imageIds[0] ?? null;

      // Upload avatar if provided
      let avatarId: number | null = null;
      if (data.avatar) {
        const avatarIds = await uploadGroupImages([data.avatar]);
        avatarId = avatarIds[0] ?? null;
      }

      // 3. Resolve category slugs → documentIds via the canonical list. If
      // the store has not fetched categories yet (rare — every page that
      // hosts the create modal calls fetchCategories on mount), do it now.
      if (data.categorySlugs.length > 0 && !get().categoriesLoaded) {
        await get().fetchCategories();
      }
      const categoryDocumentIds = data.categorySlugs
        .map((slug) => get().categories.find((c) => c.slug === slug)?.documentId)
        .filter((docId): docId is string => Boolean(docId));

      // 4. Create the group
      const groupRes = await fetch(`${baseUrl}/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          data: {
            name: data.name,
            description: data.description,
            ...(categoryDocumentIds.length > 0 && { categories: { connect: categoryDocumentIds } }),
            ...(data.websiteLink?.trim() && { websiteLink: data.websiteLink.trim() }),
            acceptingMembers: data.acceptingMembers,
            memberListVisible: data.memberListVisible,
            eventListVisible: data.eventListVisible,
            // MWBI-890 — the Create Group modal no longer sends isPrivate at
            // all; directory visibility is decoupled from list visibility, so
            // the key must be absent and Strapi's schema default (false) apply.
            // NOTE: `isPrivate: undefined` would already serialise away, since
            // JSON.stringify drops undefined-valued keys. This spread is for
            // explicitness, not a behaviour change — it states the intent at
            // the call site and survives a future move to a serialiser that
            // emits nulls. The contract is pinned by the groupStore test
            // "omits isPrivate from the POST body entirely".
            ...(data.isPrivate !== undefined && { isPrivate: data.isPrivate }),
            memberApprovalMode: data.memberApprovalMode,
            ...(imageId && { coverImage: imageId }),
            ...(imageIds.length > 0 && { images: imageIds }),
            ...(avatarId && { avatar: avatarId }),
            ...(data.rulesText !== undefined && { rulesText: data.rulesText.trim() }),
          },
        }),
      });
      if (!groupRes.ok) {
        throw new Error(await getUploadErrorMessage(groupRes, 'Failed to create group'));
      }
      const groupJson = await groupRes.json();
      const groupDocumentId = groupJson.data.documentId;

      // Refresh groups list and membership indexes. The creator becomes an
      // admin of the new group, so refresh myMemberships (gates their own
      // create/manage UI) and the site-wide index alike.
      get().fetchGroups(true);
      get().fetchMyMemberships(userId, true);
      get().fetchGroupMembers(true);

      return { success: true, groupDocumentId };
    } catch (error) {
      console.error('Error creating group:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Failed to create group' };
    }
  },

  updateGroup: async (documentId: string, data: UpdateGroupData) => {
    try {
      // Build update payload
      const updatePayload: Record<string, unknown> = {};
      if (data.name !== undefined) updatePayload.name = data.name;
      if (data.description !== undefined) updatePayload.description = data.description;
      if (data.categorySlugs !== undefined) {
        // Slug → documentId resolution. Same lazy-fetch fallback as createGroup.
        if (data.categorySlugs.length > 0 && !get().categoriesLoaded) {
          await get().fetchCategories();
        }
        const docIds = data.categorySlugs
          .map((slug) => get().categories.find((c) => c.slug === slug)?.documentId)
          .filter((docId): docId is string => Boolean(docId));
        updatePayload.categories = { set: docIds };
      }
      if (data.websiteLink !== undefined) updatePayload.websiteLink = data.websiteLink.trim();
      if (data.acceptingMembers !== undefined) updatePayload.acceptingMembers = data.acceptingMembers;
      if (data.memberListVisible !== undefined) updatePayload.memberListVisible = data.memberListVisible;
      if (data.eventListVisible !== undefined) updatePayload.eventListVisible = data.eventListVisible;
      if (data.isPrivate !== undefined) updatePayload.isPrivate = data.isPrivate;
      if (data.memberApprovalMode !== undefined) updatePayload.memberApprovalMode = data.memberApprovalMode;
      if (data.rulesText !== undefined) updatePayload.rulesText = data.rulesText.trim();

      // Handle avatar upload
      if (data.avatar) {
        const avatarIds = await uploadGroupImages([data.avatar]);
        if (avatarIds[0]) updatePayload.avatar = avatarIds[0];
      }

      // Handle multi-image edit flow (existingImageIds + newImages)
      if (data.existingImageIds !== undefined || data.newImages !== undefined) {
        const existingIds = data.existingImageIds ?? [];
        const newlyUploadedIds = data.newImages && data.newImages.length > 0
          ? await uploadGroupImages(data.newImages)
          : [];
        const allIds = [...existingIds, ...newlyUploadedIds];
        updatePayload.images = allIds;
        if (allIds.length > 0) {
          updatePayload.coverImage = allIds[0];
        }
      } else {
        // Legacy single/multi image upload path
        let imageId: number | undefined;
        let imageIds: number[] | undefined;
        if (data.images && data.images.length > 0) {
          imageIds = await uploadGroupImages(data.images);
          imageId = imageIds[0];
        } else if (data.image) {
          const uploadedFiles = await uploadGroupImages([data.image]);
          imageId = uploadedFiles[0];
        }
        if (imageId) updatePayload.coverImage = imageId;
        if (imageIds && imageIds.length > 0) updatePayload.images = imageIds;
      }

      const res = await fetch(`${baseUrl}/groups/${documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: updatePayload }),
      });

      if (!res.ok) {
        throw new Error(await getUploadErrorMessage(res, 'Failed to update group'));
      }

      // Refresh groups list
      get().fetchGroups(true);
      return { success: true };
    } catch (error) {
      console.error('Error updating group:', error);
      return { success: false, error: (error as Error).message };
    }
  },

  deleteGroup: async (documentId: string) => {
    try {
      const res = await fetch(`${baseUrl}/groups/${documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { deletedAt: new Date().toISOString() } }),
      });

      if (!res.ok) throw new Error('Failed to delete group');

      // Remove from local state
      set((state) => ({
        groups: state.groups.filter((g) => g.documentId !== documentId),
      }));

      return true;
    } catch (error) {
      console.error('Error deleting group:', error);
      return false;
    }
  },

  getUserGroups: (userId: number) => {
    const { groups, members } = get();
    const userMemberships = members.filter(m => m.userId === userId);
    return userMemberships.map(membership => {
      // Use documentId for stable matching (id can change when group is updated)
      const group = groups.find(g => g.documentId === membership.groupDocumentId);
      return group ? { group, role: membership.role } : null;
    }).filter((m): m is UserGroupMembership => m !== null);
  },

  // Current-user helper: reads `myMemberships`. Pass the signed-in user's id;
  // it cannot answer for other users (use getUserGroups for that).
  getUserRole: (userId: number, groupId: number) => {
    const { myMemberships } = get();
    const membership = myMemberships.find(m => m.userId === userId && m.groupId === groupId);
    return membership?.role ?? null;
  },

  // Current-user helper: reads `myMemberships`. Pass the signed-in user's id.
  isUserMemberOfGroup: (userId: number, groupId: number) => {
    const { myMemberships } = get();
    return myMemberships.some(m => m.userId === userId && m.groupId === groupId);
  },

  // Current-user helper: reads `myMemberships`. Pass the signed-in user's id.
  canUserCreateOpportunity: (userId: number) => {
    const { myMemberships } = get();
    return myMemberships.some(m => m.userId === userId && (m.role === 'admin' || m.role === 'editor'));
  },

  // Current-user helper: reads `myMemberships`. Pass the signed-in user's id.
  getGroupsWhereUserCanPost: (userId: number) => {
    const { groups, myMemberships } = get();
    return getGroupsWhereUserCanPostFromState(groups, myMemberships, userId);
  },

  // Current-user helper: reads `myMemberships`. Pass the signed-in user's id.
  canUserManageOpportunity: (userId: number, groupId: number) => {
    const { myMemberships } = get();
    const membership = myMemberships.find(m => m.userId === userId && m.groupId === groupId);
    return membership?.role === 'admin';
  },
    }),
    {
      name: 'group-filters',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        selectedCategory: state.selectedCategory,
        searchQuery: state.searchQuery,
      }),
    },
  ),
);

